import { useMutation } from "@apollo/client/react";
import {
  ContentStatus,
  MultipleChoiceEditData,
  MultipleChoiceEditDataVariables,
  MultipleChoiceType,
  UpdateMultipleChoiceDetails,
  UpdateMultipleChoiceDetailsVariables,
} from "__generated__/types";
import { CancelOrSubmit } from "common/buttons";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { Select } from "common/components/Select";
import { contentStatusOptions } from "common/content-status";
import { usePreviousPath } from "common/context/PreviousPathContext";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import {
  extractNullableMarkdownContent,
  markdownInput,
} from "common/input-types";
import { urls } from "common/urls";
import { adminMultipleChoiceFragment } from "domain/admin/fragments";
import gql from "graphql-tag";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";

const GET_MULTIPLE_CHOICE = gql`
  query MultipleChoiceEditData($multipleChoiceId: ID!) {
    multipleChoiceById(id: $multipleChoiceId) {
      ...AdminMultipleChoiceFragment
    }
  }
  ${adminMultipleChoiceFragment}
`;

const MUTATION = gql`
  mutation UpdateMultipleChoiceDetails(
    $input: UpdateMultipleChoiceDetailsInput!
  ) {
    updateMultipleChoiceDetails(input: $input) {
      multipleChoice {
        id
      }
    }
  }
`;

interface FormValues {
  status: ContentStatus;
  title: string;
  ingress: string;
  relevantContent: string;
}

type RouteProps = {
  multipleChoiceId: string;
};

export const MultipleChoiceEditPage: React.FC = () => {
  const { multipleChoiceId } = useParams<RouteProps>();
  const navigate = useNavigate();
  const previousPath = usePreviousPath();

  if (!multipleChoiceId) {
    throw new Error("Unexpected error: Missing multipleChoiceId");
  }

  const { data, loadingOrError } = useSimpleQuery<
    MultipleChoiceEditData,
    MultipleChoiceEditDataVariables
  >(GET_MULTIPLE_CHOICE, {
    variables: {
      multipleChoiceId,
    },
  });

  const methods = useForm<FormValues>({
    defaultValues: {
      status: data?.multipleChoiceById?.status,
    },
  });

  const [mutate, { error, loading }] = useMutation<
    UpdateMultipleChoiceDetails,
    UpdateMultipleChoiceDetailsVariables
  >(MUTATION);

  function exitEditPage() {
    if (previousPath && !previousPath.endsWith("/administration")) {
      navigate(-1);
    } else {
      navigate(urls.admin);
    }
  }

  const onSubmit = async (content: FormValues) => {
    const result = await mutate({
      variables: {
        input: {
          id: multipleChoiceId,
          status: { updatedValue: content.status },
          title: { updatedValue: content.title },
          ingress: {
            updatedValue: markdownInput(content.ingress),
          },
          relevantContent: {
            updatedValue: markdownInput(content.relevantContent),
          },
        },
      },
    });

    if (result.data) {
      exitEditPage();
    }
  };

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.multipleChoiceById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const {
    type,
    title,
    context = [],
    relevantContent,
    ingress,
  } = data.multipleChoiceById;

  return (
    <AdministrationLayout
      title={
        type == MultipleChoiceType.STANDARD
          ? "Endre flervalgsoppgave"
          : "Endre grupperingsoppgave"
      }
      context={context}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <TextField
            title="Tittel"
            fieldProps={methods.register("title", {
              required: "Du må legge til en tittel",
            })}
            defaultValue={title}
          />

          <Select
            title="Status"
            options={contentStatusOptions()}
            fieldProps={methods.register("status")}
          />

          <MarkdownField<FormValues>
            name="ingress"
            title="Introduksjonstekst"
            defaultValue={extractNullableMarkdownContent(ingress) ?? ""}
          />

          <MarkdownField<FormValues>
            name="relevantContent"
            title="Relevant innhold"
            defaultValue={extractNullableMarkdownContent(relevantContent) ?? ""}
          />

          <MutationLoadingOrError loading={loading} error={error} />

          <CancelOrSubmit
            onCancel={exitEditPage}
            submitText="Lagre"
            isLoading={loading}
          />
        </form>
      </FormProvider>
    </AdministrationLayout>
  );
};
